<template>
  <div class="container">
    <div v-if="unauthed" class="logo">
      <gl-logo-bizoope size="middle"/>
    </div>

    <!-- ポータルでログインしていない場合、通常のログイン -->
    <div v-if="unauthed" class="login-form">
      <md-field>
        <label>Login ID</label>
        <md-input v-model="loginForm.loginId" @keyup.enter="login(loginForm.companyCode, loginForm.loginId, loginForm.password)"></md-input>
      </md-field>
      <md-field>
        <label>Password</label>
        <md-input type="password" v-model="loginForm.password" @keyup.enter="login(loginForm.companyCode, loginForm.loginId, loginForm.password)"></md-input>
      </md-field>
      <p class="actions">
        <md-button class="md-primary" :disabled="loading" v-on:click="login(loginForm.companyCode, loginForm.loginId, loginForm.password)">Login</md-button>
      </p>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <p class="faq-link"><faq-link path="/tags/ログイン"/></p>
    </div>

    <!-- ポータルのログイン情報はあるがパスワード変更が済んでいない場合はSSO続行不可 -->
    <div v-if="loggedInButRequiresPasswordChange">
      <p>
        パスワードの変更が済んでいないため、まだアプリケーションはご利用いただけません。
      </p>
      <p>
        <a href="#" @click="toPasswordChange">BiZOOPeポータルページでパスワードを変更</a>してから再度お試しください。
      </p>
    </div>

    <!-- 認証済 -->
    <div v-if="authed">
      <md-icon style="color:green">check_circle_outline</md-icon>
    </div>
  </div>
</template>

<script>
import {
  getUserId,
  getLoginId,
  getAccessToken,
  getExpiresAt,
  renewAccessToken,
  remove,
} from '@/util/token'

function isInvalidCredential(response) {
  if (response.status !== 400) {
    return false
  }
  if (!response.data) {
    return true
  }
  if (!response.data.cause) {
    return true
  }

  return response.data.cause.toUpperCase() === 'INVALIDCREDENTIALS'
}

const originAllowed = new Set(
  (process.env.VUE_APP_SSO_ORIGIN_ALLOWED || '').split(','),
)

/**
 * SSOで認証するために、別アプリケーションから委譲されたときに
 * 仕様するページです。
 *
 * SSOしたいアプリケーションはこのページを開き、messageイベントを処理
 * することで、このページで取得した認証情報をもらうことができます。
 *
 * 例）
 *
 * ```javascript
 * window.addEventListener('message', e => {
 *   // このWindowに対するMessageは全て受信してしまうので、SSOを委譲したOrigin
 *   // からのMessageであるか確実にチェックする
 *   if (e.origin !== new URL(AUTH_URL).origin) {
 *     return
 *   }
 *
 *   if (
 *      !e.data.loginId ||
 *      !e.data.accessToken ||
 *      !e.data.refreshToken
 *   ) {
 *     throw new Error('認証ページから送信された認証情報が不正です')
 *   }
 *
 *   // 受信した認証情報で処理を続行
 * }, false)
 * ```
 */

const LAST_COMPANY_CODE_KEY = 'bizoo.last.companyCode'
const storage = localStorage

import FaqLink from '../components/FaqLink.vue'

export default {
  name: 'LoginSSO2',
  components: {
    FaqLink,
  },
  data() {
    return {
      /**
       * このページを開いた時点で保存されている認証情報
       */
      authInfo: {
        userId: undefined,
        companyCode: 'dummy',
        loginId: undefined,
      },
      /**
       * ログインフォームの入力値
       */
      loginForm: {
        companyCode: 'dummy',
        loginId: '',
        password: '',
      },
      /**
       * ログイン処理でのエラーメッセージ
       */
      errorMessage: undefined,
      /**
       * 認証処理を行っている最中か否か
       */
      loading: false,
      /**
       * 選択企業
       */
      companies: [],
    }
  },
  computed: {
    unauthed() {
      return !this.$store.state.user.userId
    },
    authed() {
      return (
        this.$store.state.user.userId &&
        !this.$store.state.user.requiresPasswordChange
      )
    },
    /**
     * 認証は通っているがパスワード変更が必要
     */
    loggedInButRequiresPasswordChange() {
      return (
        this.$store.state.user.userId &&
        this.$store.state.user.requiresPasswordChange
      )
    },
    targetOrigin() {
      return this.$route.query.q
    },
  },
  methods: {
    /**
     * ログイン処理
     */
    async login(companyCode, loginId, password) {
      this.loading = true
      this.errorMessage = undefined

      if (!loginId || !password /*|| !companyCode*/) {
        this.loading = false
        return
      }

      try {
        await this.$store.dispatch('login', { companyCode, loginId, password })
      } catch (error) {
        if (isInvalidCredential(error.response)) {
          this.errorMessage = error.response.data.message
          this.loginForm.password = ''
          this.loading = false
          return
        }
        throw error
      }
      storage.setItem(LAST_COMPANY_CODE_KEY, companyCode)
      this.loading = false

      if (this.loggedInButRequiresPasswordChange) {
        // パスワードの変更が済んでいないユーザーはSSOは使えない。
        return
      }

      if (!window.opener) {
        throw new Error('SSO対象ウィンドウが見つかりません')
      }

      console.log('targetOrigin', this.targetOrigin)
      console.log('originAllowed', originAllowed)
      if (
        !(
          this.targetOrigin === window.origin ||
          originAllowed.has(this.targetOrigin)
        )
      ) {
        if (
          this.targetOrigin.startsWith('http://localhost') ||
          this.targetOrigin.startsWith('http://127.0.0.1')
        ) {
          // localhostはPass
        } else {
          throw new Error(`対象外オリジン ${this.targetOrigin}`)
        }
      }

      window.opener.postMessage(
        {
          result: {
            userId: getUserId(),
            loginId: getLoginId(),
            accessToken: getAccessToken(),
          },
        },
        this.targetOrigin,
      )

      setTimeout(() => {
        window.close()
      }, 1500)
    },
    toPasswordChange() {
      window.open(`${process.env.BASE_URL}passwordupdate`)
      setTimeout(() => {
        window.close()
      }, 2000)
    },
    async handleMessage(e) {
      if (e.origin !== this.targetOrigin) {
        return
      }

      switch (e.data.command) {
        case 'getToken': {
          const expiresAt = getExpiresAt()

          if (expiresAt) {
            const now = new Date()
            const margin = 5 * 60 * 1000

            if (now.getTime() > expiresAt.getTime() - margin) {
              await renewAccessToken()
            }
          }

          e.ports[0].postMessage({
            result: {
              userId: getUserId(),
              loginId: getLoginId(),
              accessToken: getAccessToken(),
            },
          })

          break
        }
        case 'signout': {
          remove()
          e.ports[0].postMessage({ result: 'signedout' })

          break
        }
      }
    },
  },
  mounted() {
    window.addEventListener('message', this.handleMessage, false)
    if (window.parent) {
      window.parent.postMessage({ result: 'init_complete' }, this.targetOrigin)
    }
  },
}
</script>

<style scoped lang="scss">
header {
  background: var(--md-theme-default-primary, #448aff);
  color: #fff;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100; // force top

  .app-title {
    cursor: pointer;
  }
}

.header-row {
  align-items: center;
  display: flex;
  height: 56px;
  margin-left: 24px;
  margin-right: 24px;
}

.container {
  height: 100vh;
  width: 100vw;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.login-form {
  width: 300px;
}

.account-select-form {
  text-align: center;

  .main {
    margin-bottom: 32px;
  }
}

.actions {
  text-align: center;
}

.error-message {
  text-align: center;
  color: #e91e63;
}

.login-id {
  font-family: monospace;
  font-size: 24px;
  display: inline-block;
  padding: 8px;
  margin-left: 8px;
  margin-right: 8px;
}

.faq-link {
  text-align: center;
}

.logo {
  margin-bottom: 50px;
}
</style>
